import React, { useState } from "react";
import { styled, useTheme } from "@material-ui/styles";
import { Theme } from "@material-ui/core";
import { IStyledArguments } from "../../types/styled-arguments";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  Chip,
  Avatar,
  Typography as MuiTypography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  InputAdornment,
  IconButton,
} from "@material-ui/core";
import { useMutation } from "@apollo/client";
import { IPlayerWithUsers, IWorkspaceUser } from "../../types";
import {
  LINK_USER_TO_PLAYER,
  UNLINK_USER_FROM_PLAYER,
  AVAILABLE_USERS_QUERY,
  INVITE_PLAYER,
} from "./WorkspaceManagementQueries";
import UserAvatar from "../user-avatar";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import LinkIcon from "@material-ui/icons/Link";
import SearchIcon from "@material-ui/icons/Search";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import WarningIcon from "@material-ui/icons/Warning";
import EmailIcon from "@material-ui/icons/Email";
import { useQuery } from "@apollo/client";

// Styled Components
const PlayerCard = styled(Card)(({ theme }: IStyledArguments) => ({
  borderRadius: "16px",
  marginBottom: theme.spacing(2),
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
  },
}));

const PlayerHeader = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  borderBottom: "1px solid #f0f0f0",
  flexWrap: "wrap",
  [theme.breakpoints.down("sm")]: {
    gap: theme.spacing(1),
  },
}));

const PlayerButtonsContainer = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  gap: theme.spacing(1),
  flexWrap: "wrap",
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    "& button": {
      flex: 1,
      minWidth: "120px",
      fontSize: "0.75rem",
      padding: "6px 8px",
    },
  },
}));

const EmptyState = styled(Box)(({ theme }: IStyledArguments) => ({
  textAlign: "center",
  padding: theme.spacing(6),
  color: theme.palette.text.secondary,
}));

const SearchField = styled(TextField)(({ theme }: IStyledArguments) => ({
  marginBottom: theme.spacing(3),
}));

interface IPlayersManagement {
  players: IPlayerWithUsers[];
  users: IWorkspaceUser[];
  workspaceId: string;
  onRefresh: () => void;
}

const PlayersManagement: React.FC<IPlayersManagement> = ({
  players,
  workspaceId,
  onRefresh,
}) => {
  const theme = useTheme<Theme>();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPlayer, setSelectedPlayer] = useState<IPlayerWithUsers | null>(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [unlinkConfirmOpen, setUnlinkConfirmOpen] = useState(false);
  const [userToUnlink, setUserToUnlink] = useState<{ playerId: string; userId: string } | null>(
    null
  );
  
  // Estado para modal de invitación
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("parent");
  const [invitationURL, setInvitationURL] = useState("");

  // Lazy query: solo cargar usuarios disponibles cuando se abre el diálogo
  // Ahora requiere playerId para la relación N:N
  const { data: availableUsersData } = useQuery(AVAILABLE_USERS_QUERY, {
    variables: { 
      workspaceId,
      playerId: selectedPlayer?.id || "" 
    },
    skip: !workspaceId || !selectedPlayer?.id || !linkDialogOpen,
  });

  const [linkUserToPlayer, { loading: linkLoading }] = useMutation(LINK_USER_TO_PLAYER, {
    onCompleted: () => {
      onRefresh();
      setLinkDialogOpen(false);
      setSelectedPlayer(null);
    },
    onError: (error) => {
      alert(`Error linking user: ${error.message}`);
    },
  });

  const [unlinkUserFromPlayer, { loading: unlinkLoading }] = useMutation(
    UNLINK_USER_FROM_PLAYER,
    {
      onCompleted: () => {
        onRefresh();
        setUnlinkConfirmOpen(false);
        setUserToUnlink(null);
      },
      onError: (error) => {
        alert(`Error unlinking user: ${error.message}`);
      },
    }
  );

  const [invitePlayer, { loading: inviteLoading }] = useMutation(INVITE_PLAYER, {
    onCompleted: (data) => {
      if (data.invitePlayer) {
        setInvitationURL(data.invitePlayer);
        alert("Invitation sent successfully! The invitation link is displayed below.");
      }
    },
    onError: (error) => {
      alert(`Error sending invitation: ${error.message}`);
    },
  });

  const filteredPlayers = players.filter((player) => {
    const fullName = `${player.name} ${player.surname}`.toLowerCase();
    const search = searchTerm.toLowerCase();
    return fullName.includes(search) || player.position?.toLowerCase().includes(search);
  });

  const handleOpenLinkDialog = (player: IPlayerWithUsers) => {
    setSelectedPlayer(player);
    setLinkDialogOpen(true);
  };

  const handleCloseLinkDialog = () => {
    setLinkDialogOpen(false);
    setSelectedPlayer(null);
  };
  
  const handleOpenInviteDialog = (player: IPlayerWithUsers) => {
    setSelectedPlayer(player);
    setInviteDialogOpen(true);
    setInviteEmail("");
    setInviteRole("parent");
    setInvitationURL("");
  };

  const handleCloseInviteDialog = () => {
    setInviteDialogOpen(false);
    setSelectedPlayer(null);
    setInviteEmail("");
    setInvitationURL("");
  };

  const handleSendInvite = () => {
    if (selectedPlayer && inviteEmail) {
      invitePlayer({
        variables: {
          id: selectedPlayer.id,
          email: inviteEmail,
          role: inviteRole,
        },
      });
    }
  };

  const handleCopyInvitation = () => {
    navigator.clipboard.writeText(invitationURL);
    alert("Invitation link copied to clipboard!");
  };

  const handleLinkUser = (userId: string) => {
    if (selectedPlayer) {
      linkUserToPlayer({
        variables: {
          playerId: selectedPlayer.id,
          userId,
        },
      });
    }
  };

  const handleOpenUnlinkConfirm = (playerId: string, userId: string) => {
    setUserToUnlink({ playerId, userId });
    setUnlinkConfirmOpen(true);
  };

  const handleConfirmUnlink = () => {
    if (userToUnlink) {
      unlinkUserFromPlayer({
        variables: {
          playerId: userToUnlink.playerId,
          userId: userToUnlink.userId,
        },
      });
    }
  };

  const availableUsers = availableUsersData?.availableUsers || [];
  const hasPlayersWithoutUsers = players.some((p) => p.linkedUsers.length === 0);

  return (
    <Box p={3}>
      {/* Search Bar */}
      <SearchField
        fullWidth
        variant="outlined"
        placeholder="Search players by name or position..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {/* Warning for players without users */}
      {hasPlayersWithoutUsers && (
        <Card
          style={{
            marginBottom: 24,
            padding: 16,
            background: "#fff3cd",
            borderLeft: "4px solid #ffc107",
          }}
        >
          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            <WarningIcon style={{ color: "#ff9800" }} />
            <MuiTypography variant="body2">
              Some players don't have any linked users. Consider linking users to enable account access.
            </MuiTypography>
          </Box>
        </Card>
      )}

      {/* Players List */}
      {filteredPlayers.length === 0 ? (
        <EmptyState>
          <PersonAddIcon style={{ fontSize: 64, color: "#ccc" }} />
          <MuiTypography variant="h6" style={{ marginTop: 16 }}>
            {searchTerm ? "No players found" : "No players available"}
          </MuiTypography>
          <MuiTypography variant="body2" color="textSecondary">
            {searchTerm
              ? "Try adjusting your search criteria"
              : "Players will appear here once they are added to the workspace"}
          </MuiTypography>
        </EmptyState>
      ) : (
        <Grid container spacing={3}>
          {filteredPlayers.map((player) => (
            <Grid item xs={12} key={player.id}>
              <PlayerCard>
                <PlayerHeader>
                  <UserAvatar
                    user={{
                      name: player.name,
                      surname: player.surname,
                      avatarUrl: player.avatarUrl,
                    }}
                    style={{ width: 56, height: 56 }}
                  />
                  <Box flex={1}>
                    <MuiTypography variant="h6" style={{ margin: 0 }}>
                      {player.name} {player.surname}
                      {player.number && (
                        <Chip
                          label={`#${player.number}`}
                          size="small"
                          style={{ marginLeft: 8 }}
                          color="primary"
                        />
                      )}
                    </MuiTypography>
                    <MuiTypography variant="body2" color="textSecondary">
                      {player.position || "No position"} •{" "}
                      {player.team?.name || "No team assigned"}
                    </MuiTypography>
                    {player.totalMatches !== undefined && (
                      <MuiTypography variant="caption" color="textSecondary">
                        {player.totalMatches} matches • {player.totalGoals || 0} goals
                      </MuiTypography>
                    )}
                  </Box>
                  <PlayerButtonsContainer>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenLinkDialog(player)}
                    >
                      Link User
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      size="small"
                      startIcon={<EmailIcon />}
                      onClick={() => handleOpenInviteDialog(player)}
                    >
                      Invite
                    </Button>
                  </PlayerButtonsContainer>
                </PlayerHeader>

                <CardContent>
                  <MuiTypography
                    variant="subtitle2"
                    style={{ marginBottom: 12, fontWeight: 600, color: "#333" }}
                  >
                    Linked Users ({player.linkedUsers.length})
                  </MuiTypography>
                  {player.linkedUsers.length === 0 ? (
                    <Box
                      p={2}
                      style={{
                        background: "#f9f9f9",
                        borderRadius: 8,
                        textAlign: "center",
                        border: "1px dashed #ddd",
                      }}
                    >
                      <PersonAddIcon style={{ fontSize: 32, color: "#ccc", marginBottom: 8 }} />
                      <MuiTypography variant="body2" color="textSecondary">
                        No users linked yet
                      </MuiTypography>
                      <MuiTypography variant="caption" color="textSecondary">
                        Click "Link User" to add access
                      </MuiTypography>
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      {player.linkedUsers.map((user) => (
                        <Grid item xs={12} sm={6} key={user.id}>
                          <Box
                            style={{
                              background: "#f5f7fa",
                              borderRadius: 8,
                              padding: "12px 16px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              border: "1px solid #e3e8ef",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = "#eef2f7";
                              e.currentTarget.style.borderColor = "#cbd5e0";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "#f5f7fa";
                              e.currentTarget.style.borderColor = "#e3e8ef";
                            }}
                          >
                            <Box display="flex" alignItems="center" style={{ gap: 12, flex: 1 }}>
                              <Avatar
                                style={{
                                  width: 36,
                                  height: 36,
                                  background: theme.palette.primary.main,
                                  fontSize: "0.875rem",
                                }}
                              >
                                {user.name.charAt(0)}
                                {user.surname.charAt(0)}
                              </Avatar>
                              <Box flex={1}>
                                <MuiTypography
                                  variant="body2"
                                  style={{ fontWeight: 600, lineHeight: 1.2 }}
                                >
                                  {user.name} {user.surname}
                                </MuiTypography>
                                <MuiTypography
                                  variant="caption"
                                  color="textSecondary"
                                  style={{ display: "block" }}
                                >
                                  {user.role}
                                </MuiTypography>
                              </Box>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenUnlinkConfirm(player.id, user.id)}
                              style={{ color: "#e53e3e" }}
                              title="Unlink user"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </CardContent>
              </PlayerCard>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Link User Dialog */}
      <Dialog
        open={linkDialogOpen}
        onClose={handleCloseLinkDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            <LinkIcon color="primary" />
            Link User to {selectedPlayer?.name} {selectedPlayer?.surname}
          </Box>
        </DialogTitle>
        <DialogContent>
          {availableUsers.length === 0 ? (
            <EmptyState>
              <PersonAddIcon style={{ fontSize: 48, color: "#ccc" }} />
              <MuiTypography variant="body1" style={{ marginTop: 16 }}>
                No available users
              </MuiTypography>
              <MuiTypography variant="body2" color="textSecondary">
                All users are already linked to players
              </MuiTypography>
            </EmptyState>
          ) : (
            <List>
              {availableUsers.map((user: IWorkspaceUser) => (
                <ListItem
                  key={user.id}
                  button
                  onClick={() => handleLinkUser(user.id)}
                  disabled={linkLoading}
                >
                  <ListItemAvatar>
                    <UserAvatar
                      user={{
                        name: user.name,
                        surname: user.surname,
                        avatarUrl: user.avatarUrl,
                      }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={`${user.name} ${user.surname}`}
                    secondary={`${user.email} • ${user.role}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLinkDialog} disabled={linkLoading}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unlink Confirmation Dialog */}
      <Dialog
        open={unlinkConfirmOpen}
        onClose={() => setUnlinkConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Unlink</DialogTitle>
        <DialogContent>
          <MuiTypography>
            Are you sure you want to unlink this user from the player? The user will lose
            access to this player's profile.
          </MuiTypography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setUnlinkConfirmOpen(false)}
            disabled={unlinkLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmUnlink}
            color="secondary"
            variant="contained"
            disabled={unlinkLoading}
          >
            {unlinkLoading ? "Unlinking..." : "Unlink"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite User Dialog */}
      <Dialog
        open={inviteDialogOpen}
        onClose={handleCloseInviteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Invite New User to {selectedPlayer?.name} {selectedPlayer?.surname}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" style={{ gap: 16, paddingTop: 8 }}>
            <MuiTypography variant="body2" color="textSecondary">
              Send an invitation email to a new user to give them access to this player's profile.
            </MuiTypography>
            
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              variant="outlined"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="user@example.com"
              disabled={!!invitationURL}
            />
            
            <TextField
              fullWidth
              select
              label="User Role"
              variant="outlined"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              SelectProps={{
                native: true,
              }}
              disabled={!!invitationURL}
            >
              <option value="parent">Parent</option>
              <option value="coach">Coach</option>
              <option value="Manager">Manager</option>
            </TextField>

            {invitationURL && (
              <Box>
                <MuiTypography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                  Invitation Link:
                </MuiTypography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  value={invitationURL}
                  InputProps={{
                    readOnly: true,
                  }}
                />
                <Box mt={2}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleCopyInvitation}
                    startIcon={<LinkIcon />}
                  >
                    Copy Link
                  </Button>
                </Box>
                <MuiTypography variant="caption" color="textSecondary" style={{ marginTop: 8, display: 'block' }}>
                  An email has been sent. You can also copy the invitation link and share it via WhatsApp or text message.
                </MuiTypography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInviteDialog} disabled={inviteLoading}>
            {invitationURL ? "Close" : "Cancel"}
          </Button>
          {!invitationURL && (
            <Button
              onClick={handleSendInvite}
              color="primary"
              variant="contained"
              disabled={inviteLoading || !inviteEmail}
            >
              {inviteLoading ? "Sending..." : "Send Invitation"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlayersManagement;
